import { motion, AnimatePresence } from 'framer-motion';
import { Phone, Mail, MapPin, Clock, ChevronDown } from 'lucide-react';
import { useState } from 'react';
import Section from '../components/ui/Section';
import SEO from '../components/ui/SEO';
import ContactForm from '../components/ContactForm';
import { companyInfo } from '../data';

const AccordionItem = ({ question, answer, isOpen, onClick }) => {
  return (
    <div className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden transition-all duration-300 hover:border-blue-200">
      <button
        onClick={onClick}
        className="flex w-full items-center justify-between p-6 text-left group focus:outline-none"
        aria-expanded={isOpen}
      >
        <span className={`text-lg font-bold transition-colors tracking-wide ${isOpen ? 'text-[#1d398e]' : 'text-slate-900'}`}>
          {question}
        </span>
        <ChevronDown 
          className={`w-5 h-5 transition-all duration-300 ${isOpen ? 'rotate-180 text-[#1d398e]' : 'text-slate-400'}`} 
        />
      </button>
      <AnimatePresence initial={false}>
        {isOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3, ease: "easeInOut" }}
          >
            <div className="px-6 pb-6 text-slate-600 leading-relaxed">
              {answer}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default function Contact() {
  const [openIndex, setOpenIndex] = useState(null);

  const handleToggle = (index) => {
    setOpenIndex(prevIndex => prevIndex === index ? null : index);
  };

  const faqs = [
    { q: "Hvor hurtigt kan I levere en container?", a: "Vi kan oftest levere en container inden for 24 timer på hverdage i vores primære dækningsområde. Kontakt os gerne direkte for hastesager." },
    { q: "Kører I i hele Danmark?", a: "Ja, vi udfører transportopgaver i hele landet. Selvom vores base er i Østjylland, har vi ruter og samarbejdspartnere, der dækker hele Danmark." },
    { q: "Kan jeg få en fast pris på en transportopgave?", a: "Ja, vi giver altid gerne et uforpligtende tilbud med en fast pris. Det sikrer gennemsigtighed og tryghed for dig som kunde." },
    { q: "Hvilke typer containere udlejer I?", a: "Vi tilbyder containerudlejning til ethvert behov og har løsninger til både private byggeprojekter og store erhvervsopgaver." }
  ];

  const contactDetails = [
    {
      title: "Ring til os",
      value: companyInfo?.phone || "21 62 94 36",
      href: `tel:${(companyInfo?.phone || "21 62 94 36").replace(/\s/g, '')}`,
      icon: Phone
    },
    {
      title: "Send en mail",
      value: companyInfo?.email || "psg@elromail.dk",
      href: `mailto:${companyInfo?.email || "psg@elromail.dk"}`,
      icon: Mail
    },
    {
      title: "Besøg os",
      value: companyInfo?.address || "Erslevvej 45, 8370 Hadsten",
      href: "https://maps.google.com/?q=" + encodeURIComponent(companyInfo?.address || "Erslevvej 45, 8370 Hadsten"),
      icon: MapPin
    },
    {
      title: "Åbningstider",
      value: "Mandag - Fredag: 07:00 - 17:00",
      icon: Clock
    }
  ];

  return (
    <>
      <SEO 
        title="Kontakt" 
        description="Kontakt NR. GALTEN VOGNMANDSFORRETNING ApS for et uforpligtende tilbud på transport, distribution eller containerudlejning. Vi står klar til at hjælpe dig." 
      />

      {/* Hero */}
      <section className="pt-40 pb-20 bg-slate-50 text-slate-900 relative overflow-hidden">
        {/* Skewed Background Element */}
        <div className="absolute top-0 right-0 w-1/2 h-full bg-[#1d398e] hidden lg:block skew-x-[-12deg] translate-x-20 z-0" />
        
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-3xl"
          >
            <div className="inline-flex items-center space-x-2 bg-blue-50 text-[#1d398e] px-4 py-2 rounded-full text-sm font-bold mb-6">
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#1d398e] opacity-75"></span>
                <span className="relative inline-flex rounded-full h-2 w-2 bg-[#1d398e]"></span>
              </span>
              <span className="tracking-wide uppercase">Kontakt os i dag</span>
            </div>
            <h1 className="text-4xl md:text-6xl font-black mb-6 leading-[1.1] tracking-wide text-slate-900">
              Lad os <span className="text-[#1d398e]">hjælpe dig</span>
            </h1>
            <p className="text-xl text-slate-600 leading-relaxed max-w-2xl">
              Har du spørgsmål eller brug for et uforpligtende tilbud? Vi sidder klar til at hjælpe dig med din næste transportopgave.
            </p>
          </motion.div>
        </div>
      </section>

      <Section>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
          {/* Contact Info Cards */}
          <div className="lg:col-span-1 space-y-6">
            {contactDetails.map((detail, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: index * 0.1 }}
                className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100 flex items-start space-x-4"
              >
                <div className="w-12 h-12 bg-blue-50 rounded-xl flex items-center justify-center text-blue-900 shrink-0">
                  <detail.icon className="w-6 h-6" />
                </div>
                <div>
                  <h3 className="text-sm font-bold text-gray-500 uppercase tracking-wider mb-1">{detail.title}</h3>
                  {detail.href ? (
                    <a href={detail.href} className="text-lg font-bold text-blue-900 hover:text-blue-700 transition-colors">
                      {detail.value}
                    </a>
                  ) : (
                    <p className="text-lg font-bold text-blue-900">{detail.value}</p>
                  )}
                </div>
              </motion.div>
            ))}

            {/* Map Placeholder */}
            <div className="bg-gray-100 rounded-2xl h-64 overflow-hidden relative group">
              <iframe 
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2212.434316109923!2d10.0504143!3d56.322111!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x464c198751555555%3A0x5555555555555555!2sErslevvej%2045%2C%208370%20Hadsten!5e0!3m2!1sda!2sdk!4v1620000000000!5m2!1sda!2sdk" 
                width="100%" 
                height="100%" 
                style={{ border: 0 }} 
                allowFullScreen="" 
                loading="lazy"
                title="Kort over lokation"
                className="grayscale hover:grayscale-0 transition-all duration-500"
              ></iframe>
            </div>
          </div>

          {/* Contact Form */}
          <div className="lg:col-span-2">
            <ContactForm />
          </div>
        </div>
      </Section>

      {/* FAQ Section */}
      <Section light>
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-xl md:text-4xl font-black text-slate-900 mb-4 tracking-wide">
              Ofte stillede <span className="text-[#1d398e]">spørgsmål</span>
            </h2>
            <p className="text-slate-600">Find hurtige svar på de mest gængse spørgsmål om vores services.</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {faqs.map((faq, index) => (
              <AccordionItem
                key={index}
                question={faq.q}
                answer={faq.a}
                isOpen={openIndex === index}
                onClick={() => handleToggle(index)}
              />
            ))}
          </div>
        </div>
      </Section>
    </>
  );
}
